import React, { useCallback, useMemo, useState } from 'react';
import { GetSongTiktokAudioStatsV2Params } from '@round/api';
import PaginationTable from '../../../../../ui/DataDisplay/Tables/PaginationTable/PaginationTable';
import { GenericDropdownOption } from '../../../../../App.types';
import { AudioPlayerProvider } from '../../../../AudioPlayer/AudioPlayerContext';
import SearchInput from '../../../../../ui/DataEntry/SearchInput/SearchInput';
import styles from './TopTiktokAudios.module.css';
import Select from '../../../../../ui/DataEntry/Select/Select';
import { StylesConfig, ValueType } from 'react-select';
import { formatNumberToKNotation, numberWithCommas, roundTo2Dp } from '../../../../../helpers';
import BarsSmallChart from '../../../../../ui/DataDisplay/Charts/BarsSmallChart/BarsSmallChart';
import { CellProps, Column } from 'react-table';
import cn from 'classnames';
import Skeleton from '../../../../../ui/Skeleton/Skeleton';
import TruncatedTextCell from '../../../../../SharedComponents/TableComponents/TruncatedTextCell/TruncatedTextCell';
import Button from '../../../../../ui/Buttons/Button/Button';
import LeftArrowIcon from '../../../../../SharedComponents/svg/Icons/LeftArrowIcon';
import { TopTiktokAudiosTableRow } from '../SongDetailsModal.types';
import { ReactComponent as CloseIcon } from '../../../../../assets/Close.svg';
import { useCheckUserGroupsAccess } from '../../../../Auth/hooks/useCheckUserGroupsAccess';
import TiktokAudioModal from '../../../../TikTok/audios/features/details/TiktokAudioModal/TiktokAudioModal';
import TiktokAudioBadge from '../../../../TikTok/audios/components/TiktokAudioBadge/TiktokAudioBadge';

export type TopTiktokAudiosTableProps = {
    rows: TopTiktokAudiosTableRow[];
    count: number;
    loading: boolean;
    search: string;
    setSearch: (search: string) => void;
    order: GenericDropdownOption<GetSongTiktokAudioStatsV2Params['ordering']>;
    setOrder: (order: ValueType<GenericDropdownOption<GetSongTiktokAudioStatsV2Params['ordering']>, false>) => void;
    page: number;
    setPage: (page: number) => void;
    pageSize: number;
    setPageSize: (pageSize: number | undefined) => void;
    sortingOptions: GenericDropdownOption<GetSongTiktokAudioStatsV2Params['ordering']>[];
    onRemoveAudioClicked: (audioId: number) => void;
};

const isValidKey = (value: string | undefined): value is keyof TopTiktokAudiosTableRow =>
    (value !== undefined && value === 'video_count') ||
    value === 'video_count_daily_change' ||
    value === 'video_count_daily_change_relative' ||
    value === 'video_count_weekly_change' ||
    value === 'video_count_weekly_change_relative' ||
    value === 'alternate_name' ||
    value === 'author_name' ||
    value === 'title' ||
    value === 'is_original' ||
    value === 'tiktok_id' ||
    value === 'image_url' ||
    value === 'audioUrl' ||
    value === 'clientMonitoredAudioRelation';

const sortingSelectStyles: StylesConfig = {
    control: (base) => ({
        ...base,
        borderColor: '#C2CFE0',
    }),
    valueContainer: (base) => ({
        ...base,
        minWidth: '10rem',
    }),
    singleValue: (base) => ({
        ...base,
        color: '#58698E',
        fontWeight: 600,
        fontSize: '0.75rem',
    }),
    menuList: (base) => ({
        ...base,
        color: '#58698E',
        fontWeight: 600,
        fontSize: '0.75rem',
    }),
};

const TopTiktokAudiosTable = ({
    loading,
    page,
    setPage,
    pageSize,
    setPageSize,
    search,
    rows,
    setSearch,
    count,
    order,
    setOrder,
    sortingOptions,
    onRemoveAudioClicked,
}: TopTiktokAudiosTableProps) => {
    const hasUserAdminGroup = useCheckUserGroupsAccess(['user_admin']);
    const [isAudioModalOpen, setIsAudioModalOpen] = useState(false);
    const [audioIdForAudioModal, setAudioIdForAudioModal] = useState<number>();

    const [audiosRowsDisplayed, setAudiosRowsDisplayed] = useState(0);
    const audiosRecordsShowingLabel = `displaying ${audiosRowsDisplayed} out of ${numberWithCommas(count)} audio${
        count === 1 ? '' : 's'
    }`;

    const formatVideoCountBadgeValue = useCallback(
        (n: number) => {
            if (order?.value?.includes('relative')) {
                return `${roundTo2Dp(n * 100)}%`;
            }

            return formatNumberToKNotation(n);
        },
        [order?.value]
    );

    const columns: Column<TopTiktokAudiosTableRow>[] = useMemo(() => {
        const columns: Column<TopTiktokAudiosTableRow>[] = [
            {
                Header: 'Sound',
                accessor: 'title',
                Cell: ({ row: { original } }) => (
                    <TiktokAudioBadge
                        loading={loading}
                        audio={{
                            audioPlayUrl: original.audioUrl,
                            title: original.title,
                            tiktokId: original.tiktok_id,
                            trackingType: original.tracking_type,
                            clientMonitoredAudioRelationships: original.clientMonitoredAudioRelation,
                            imageUrl: original.imageUrl,
                            isOriginal: original.is_original,
                        }}
                    />
                ),
            },
            {
                Header: 'Author',
                accessor: 'author_name',
                Cell: ({ value }) =>
                    loading ? (
                        <Skeleton width="75%" />
                    ) : (
                        <TruncatedTextCell className={styles.authorName}>{value}</TruncatedTextCell>
                    ),
            },
            {
                Header: 'Video',
                accessor: 'video_count',
                Cell: ({ value, row: { original } }) => {
                    if (loading) {
                        return <Skeleton width="75%" />;
                    }

                    const orderByValue = order.value?.charAt(0) === '-' ? order.value.slice(1) : order.value;
                    const badgeValue =
                        orderByValue && isValidKey(orderByValue) && orderByValue !== 'video_count'
                            ? original[orderByValue]
                            : null;

                    return (
                        <>
                            <span className={styles.videoCount}>{formatNumberToKNotation(value)}</span>
                            {typeof badgeValue === 'number' && (
                                <span
                                    className={cn(styles.growthBadge, {
                                        [styles.increasing]: badgeValue >= 0,
                                        [styles.decreasing]: badgeValue < 0,
                                    })}
                                >
                                    {badgeValue > 0 && '+'}
                                    {formatVideoCountBadgeValue(badgeValue)}
                                </span>
                            )}
                        </>
                    );
                },
            },
            {
                Header: 'Growth',
                accessor: 'dailyGrowth',
                Cell: ({ value }) => {
                    if (loading) {
                        return <Skeleton />;
                    }

                    return (
                        <div className={styles.dailyGrowth}>
                            <BarsSmallChart values={value} color="linear-gradient(180deg, #93AAFF 0%, #6BC1FF 100%)" />
                        </div>
                    );
                },
            },
        ];

        if (hasUserAdminGroup) {
            columns.push({
                Header: '',
                id: 'removeAudioFromSong',
                Cell: ({ row: { original } }: CellProps<TopTiktokAudiosTableRow>) => {
                    if (loading) {
                        return <Skeleton />;
                    }

                    return (
                        <Button
                            className={styles.removeAudioButton}
                            onClick={(e) => {
                                e.stopPropagation();
                                onRemoveAudioClicked(original.music_id);
                            }}
                        >
                            <CloseIcon />
                        </Button>
                    );
                },
            });
        }

        return columns;
    }, [hasUserAdminGroup, loading, order, formatVideoCountBadgeValue, onRemoveAudioClicked]);

    return (
        <div>
            <div className={styles.titles}>
                <p className={styles.title}>Top Audios</p>
                <p className={styles.subtitle}>{loading ? <Skeleton width="20rem" /> : audiosRecordsShowingLabel}</p>
            </div>
            <div className={styles.header}>
                <div className={styles.headerLeft}>
                    <SearchInput className={styles.searchInput} value={search} onChange={setSearch} />
                </div>
                <div className={styles.headerRight}>
                    <label className={styles.label}>Sort by</label>
                    <div data-test-id="sort-container">
                        <Select
                            isSearchable={false}
                            styles={sortingSelectStyles}
                            value={order}
                            options={sortingOptions}
                            onChange={(value) => setOrder(value ?? sortingOptions[1])}
                        />
                    </div>
                </div>
            </div>
            <AudioPlayerProvider>
                <PaginationTable
                    tableClassName={styles.table}
                    loading={loading}
                    data={rows}
                    columns={columns}
                    count={count}
                    page={page}
                    setPage={setPage}
                    pageSize={pageSize}
                    setPageSize={(size) => setPageSize(size ?? 10)}
                    onPaginationChange={(state) => setAudiosRowsDisplayed(state.pageRows.length)}
                    onRowClick={(row) => {
                        setAudioIdForAudioModal(row.music_id);
                        setIsAudioModalOpen(true);
                    }}
                />

                <TiktokAudioModal
                    modalClassName={styles.audioModal}
                    isModalOpen={isAudioModalOpen}
                    closeModal={() => {
                        setIsAudioModalOpen(false);
                        setAudioIdForAudioModal(undefined);
                    }}
                    audioId={audioIdForAudioModal}
                    renderCloseButton={(close) => (
                        <Button
                            className={styles.audioModalBackButton}
                            contentClassName={styles.audioModalBackButtonContent}
                            onClick={close}
                        >
                            <LeftArrowIcon width={10} height={10} className={styles.audioModalBackButtonIcon} /> Back
                        </Button>
                    )}
                />
            </AudioPlayerProvider>
        </div>
    );
};

export default TopTiktokAudiosTable;
